import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { PAGE_SIZE_OPTIONS } from '../../../shared/components/ui/pretty-select/common-options';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { UserCellComponent } from '../../../shared/components/ui/user-cell/user-cell.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { rsql, rsqlTextSearch } from '../../../shared/helpers/rsql';
import { RentalsService } from '../../../shared/services/rentals.service';
import {
  RENTAL_STATUS_META,
  Rental,
  RentalStatus,
} from '../../../shared/types/rental.types';

/** Server-side RSQL search fields. Path nav for vehicle/customer relations. */
const RENTALS_SEARCH_FIELDS = [
  'pickupLocation',
  'returnLocation',
  'vehicle.licencePlate',
  'vehicle.make.entityCode',
  'vehicle.model.entityCode',
] as const;

const STATUS_OPTIONS: PrettySelectOption<RentalStatus | null>[] = [
  { value: 'PREPENDING', label: 'Pré-réservation' },
  { value: 'PENDING', label: 'En attente' },
  { value: 'CONFIRMED', label: 'Confirmée' },
  { value: 'STARTED', label: 'En cours' },
  { value: 'FINISHED', label: 'Terminée' },
  { value: 'CANCELLED', label: 'Annulée' },
];

/** Row view-model with precomputed vehicle name (avoid recomputing in CD). */
interface RentalRow extends Rental {
  _vehicleName: string;
  _vehicleSubtitle: string;
}

@Component({
  selector: 'app-rentals-list',
  imports: [LocalizedDatePipe, 
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    PageHeaderComponent,
    DataStateComponent,
    PrettySelectComponent,
    UserCellComponent,
  ],
  templateUrl: './rentals-list.component.html',
})
export class RentalsListComponent implements OnInit {
  private readonly rentalsService = inject(RentalsService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly rentals = signal<RentalRow[]>([]);
  readonly totalItems = signal(0);
  readonly totalPages = signal(0);
  readonly page = signal(1);
  readonly size = signal(10);

  readonly searchCtrl = this.fb.control('');
  /** `null` means "no filter applied". */
  readonly statusCtrl = this.fb.control<RentalStatus | null>(null);

  readonly statusOptions = STATUS_OPTIONS;
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly statusMeta = RENTAL_STATUS_META;

  readonly pageWindow = computed<number[]>(() => {
    const total = this.totalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    const current = this.page();
    const window = 5;
    let start = Math.max(1, current - Math.floor(window / 2));
    const end = Math.min(total, start + window - 1);
    start = Math.max(1, end - window + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.page.set(1);
        this.load();
      });

    this.statusCtrl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.page.set(1);
        this.load();
      });

    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const search = rsql.and(
      rsqlTextSearch(RENTALS_SEARCH_FIELDS, this.searchCtrl.value),
      rsql.eq('rentalStatusEnum', this.statusCtrl.value),
    );

    this.rentalsService
      .list({
        page: this.page(),
        size: this.size(),
        search: search ?? undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const rows: RentalRow[] = (res.data ?? []).map((r) => ({
            ...r,
            _vehicleName: this.vehicleName(r),
            _vehicleSubtitle: this.vehicleSubtitle(r),
          }));
          this.rentals.set(rows);
          this.totalItems.set(res.totalItems ?? 0);
          this.totalPages.set(res.totalPages ?? 0);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.rentals.set([]);
          this.totalItems.set(0);
          this.totalPages.set(0);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages() || p === this.page()) return;
    this.page.set(p);
    this.load();
  }

  changeSize(s: number): void {
    if (!s || s === this.size()) return;
    this.size.set(s);
    this.page.set(1);
    this.load();
  }

  private vehicleName(r: Rental): string {
    const v = r.vehicle;
    if (!v) return `Location #${r.id}`;
    const make = v.make?.entityCode ?? v.make?.name ?? '';
    const model = v.model?.entityCode ?? v.model?.name ?? '';
    const name = [make, model].filter(Boolean).join(' ');
    return name || `Véhicule #${v.id ?? '?'}`;
  }

  private vehicleSubtitle(r: Rental): string {
    const plate = r.vehicle?.licencePlate;
    return plate ? plate : '—';
  }
}
